import axiosInstance from "../components/api/axiosInstance";

/**
 * Fetch all office services.
 * GET /office-services
 * @returns {Promise<Array>} Array of office services
 */
export async function fetchOfficeServices() {
  try {
    const response = await axiosInstance.get("/office-services");
    const raw = response?.data;
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    if (raw?.office_services && Array.isArray(raw.office_services)) return raw.office_services;
    if (raw?.results && Array.isArray(raw.results)) return raw.results;
    return [];
  } catch (err) {
    console.error("Error fetching office services:", err);
    return [];
  }
}

/**
 * Fetch all support contacts / office services.
 * Uses /office-services for Service tab data.
 * @returns {Promise<Array>} Array of office services
 */
export async function fetchSupportContacts(contactType = null) {
  const list = await fetchOfficeServices();
  return list;
}

/**
 * Fetch a single office service by ID.
 * GET /office-service/:id
 * @param {number|string} id - Office service ID
 * @returns {Promise<Object|null>} Office service object or null
 */
export async function fetchOfficeServiceById(id) {
  try {
    const response = await axiosInstance.get(`/office-service/${id}`);
    const raw = response?.data;
    return raw ?? null;
  } catch (err) {
    console.error("Error fetching office service by id:", err);
    return null;
  }
}

/**
 * Create a new office service.
 * POST /create-office-service
 * @param {Object} payload - Office service data
 * @returns {Promise<Object>} Created office service
 */
export async function createOfficeService(payload) {
  const response = await axiosInstance.post("/create-office-service", payload);
  return response?.data ?? response;
}

/**
 * Update an office service by ID.
 * PUT /update-office-service/:id
 * Request: { Service_Name, Service_Category, Contact_Name, Contact_Phone, Alt_Phone, Support_Email, Support_Hours, Support_Days, Notes, Status, Toll_Free_Number, Updated_By }
 * @param {number|string} id - Office service ID
 * @param {Object} payload - Update payload
 * @returns {Promise<Object>} Updated office service
 */
export async function updateOfficeService(id, payload) {
  const response = await axiosInstance.put(`/update-office-service/${id}`, payload);
  const raw = response?.data;
  return raw?.data ?? raw;
}

/**
 * Delete an office service by ID.
 * DELETE /office-service/:id
 * Response: { Id, message: "Office service deleted" }
 * @param {number|string} id - Office service ID
 * @returns {Promise<Object>} Response
 */
export async function deleteOfficeService(id) {
  const response = await axiosInstance.delete(`/office-service/${id}`);
  return response?.data ?? response;
}

/**
 * Fetch all office staff.
 * GET /office-staff
 * Response: Array of staff objects with Staff_Id, Staff_Name, Staff_Role, Phone_Number, Email, Shift_Start, Shift_End, Working_Days, Supervisor_Name, Status, etc.
 * @returns {Promise<Array>} Array of office staff
 */
export async function fetchOfficeStaff() {
  try {
    const response = await axiosInstance.get("/office-staff");
    const raw = response?.data;
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    if (raw?.office_staff && Array.isArray(raw.office_staff)) return raw.office_staff;
    if (raw?.results && Array.isArray(raw.results)) return raw.results;
    return [];
  } catch (err) {
    console.error("Error fetching office staff:", err);
    return [];
  }
}

/**
 * Fetch a single office staff by ID.
 * Tries GET /create-office-staff/:id first, then GET /office-staff/:id as fallback.
 * Response: { Staff_Id, Staff_Name, Staff_Role, ... } or wrapped in { data: {...} }
 * @param {number|string} id - Office staff ID
 * @returns {Promise<Object|null>} Office staff object or null
 */
export async function fetchOfficeStaffById(id) {
  if (id == null || id === "") return null;
  const idStr = String(id);

  const extractStaff = (raw) => {
    if (!raw) return null;
    if (raw.Staff_Id != null || raw.staff_id != null || raw.Staff_Name != null) return raw;
    if (raw?.data && (raw.data.Staff_Id != null || raw.data.Staff_Name != null)) return raw.data;
    return null;
  };

  const endpoints = [`/create-office-staff/${idStr}`, `/office-staff/${idStr}`];
  for (const url of endpoints) {
    try {
      const response = await axiosInstance.get(url);
      const raw = response?.data;
      const staff = extractStaff(raw);
      if (staff) return staff;
    } catch (err) {
      if (err?.response?.status === 404) continue;
      console.error("Error fetching office staff by id:", err);
      continue;
    }
  }
  return null;
}

/** Remove null/undefined/empty string values - some backends reject null */
function cleanPayload(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v != null && v !== "") out[k] = v;
  }
  return out;
}

/**
 * Create a new office staff.
 * POST /create-office-staff
 * @param {Object} payload - Office staff data
 * @returns {Promise<Object>} Created office staff
 */
export async function createOfficeStaff(payload) {
  const cleaned = cleanPayload(payload);
  const response = await axiosInstance.post("/create-office-staff", cleaned);
  return response?.data ?? response;
}

/**
 * Update an office staff by ID.
 * PUT /update-office-staff/:id
 * Request: { Staff_Name, Staff_Role, Phone_Number, Alternate_Phone, Email, Shift_Start, Shift_End, Working_Days, Supervisor_Name, Remarks, Status, Updated_By }
 * @param {number|string} id - Office staff ID
 * @param {Object} payload - Update payload
 * @returns {Promise<Object>} Updated office staff
 */
export async function updateOfficeStaff(id, payload) {
  const cleaned = cleanPayload(payload);
  const response = await axiosInstance.put(`/update-office-staff/${id}`, cleaned);
  const raw = response?.data;
  return raw?.data ?? raw ?? response?.data;
}

/**
 * Delete an office staff by ID.
 * DELETE /delete-office-staff/:id
 * Response: { Staff_Id, message: "Office staff deleted" }
 * @param {number|string} id - Office staff ID
 * @returns {Promise<Object>} Response
 */
export async function deleteOfficeStaff(id) {
  const response = await axiosInstance.delete(`/delete-office-staff/${id}`);
  return response?.data ?? response;
}
